/** Minimal i18n: a flat key table plus a `t()` bound to the current locale. */
import { useCallback, useEffect, useState } from 'preact/hooks';

export const LOCALES = ['zh', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

const STRINGS = {
  zh: {
    'app.title': 'PixelOE Web',
    'app.github': 'GitHub',

    'group.size': '尺寸',
    'group.outline': '轮廓',
    'group.downscale': '降采样',
    'group.sharpen': '锐化',
    'group.color': '色彩',
    'group.output': '高级',

    'field.targetW': '目标宽度',
    'field.targetH': '目标高度',
    'field.bind': '绑定纵横比',
    'field.pixelSize': '像素大小',
    'field.thickness': '强度',
    'field.mode': '降采样模式',
    'field.sharpenMode': '锐化模式',
    'field.doColorMatch': '色彩匹配',
    'field.doQuant': '颜色量化',
    'field.numColors': '颜色数',
    'field.quantMode': '量化模式',
    'field.ditherMode': '抖动模式',
    'field.noPostUpscale': '输出原始小图',
    'field.output': '输出尺寸',

    'thickness.off': '关闭',
    'presets': '预设',
    'preset.default': '默认',
    'preset.gba': 'GBA 风',
    'preset.detail': '高细节',
    'preset.lofi': '低保真',

    'action.upload': '上传图片',
    'action.open': '打开图片',
    'action.exportPng': '导出 PNG',
    'action.exportWebp': '导出 WebP',
    'action.copy': '复制',
    'action.copied': '已复制',
    'action.copyFailed': '复制失败',
    'action.shareUrl': '参数 → URL',
    'action.urlCopied': '链接已复制',
    'action.reset': '重置',

    'drop.title': '拖入图片，或点击选择',
    'drop.hint': '支持拖拽与粘贴（Ctrl+V），格式 PNG / JPEG / WebP',
    'drop.examples': '试试示例图',
    'drop.needImage': '先载入一张图片',

    'viewer.holdForOriginal': '按住空格查看原图',
    'viewer.splitHint': '拖动分割线对比',
    'viewer.zoomIn': '放大',
    'viewer.zoomOut': '缩小',
    'viewer.resetZoom': '回到 100%',
    'viewer.fit': '适应窗口',
    'viewer.original': '原图',
    'viewer.result': '结果',

    'status.size': '尺寸',
    'status.time': '耗时',
    'status.working': '工作分辨率',
    'status.zoom': '缩放',
    'status.exact': '精确',
    'status.reduced': '显存受限',
    'status.clamped': '超出显存预算，已自动降低工作分辨率',
    'status.exporting': '导出中',
    'status.rendering': '计算中',
    'status.iterations': 'k-means 轮次',

    'error.title': '出错了',
    'error.unsupported': '你的浏览器不支持 WebGL2 浮点渲染',
    'error.unsupportedHint':
      '本应用需要 WebGL2 与 EXT_color_buffer_float 扩展。请更新浏览器或换用桌面版 Chrome / Firefox / Safari。',

    'help.title': '快捷键',
    'help.original': '按住查看原图',
    'help.fit': '适应窗口',
    'help.actual': '实际像素（100%）',
    'help.zoom': '放大 / 缩小，共 20 档',
    'help.export': '导出 PNG',
    'help.wheel': '滚轮',
    'help.wheelHint': '缩放',
    'help.drag': '拖拽',
    'help.dragHint': '平移画面',
    'help.divider': '拖动中线',
    'help.dividerHint': '前后对比',
    'action.close': '关闭',

    'shortcut.space': '空格',
    'shortcut.fit': 'F',
    'shortcut.actual': '1',
    'shortcut.export': 'Ctrl+E',
    'shortcuts': '快捷键',
  },
  en: {
    'app.title': 'PixelOE Web',
    'app.github': 'GitHub',

    'group.size': 'Size',
    'group.outline': 'Outline',
    'group.downscale': 'Downscale',
    'group.sharpen': 'Sharpen',
    'group.color': 'Colour',
    'group.output': 'Advanced',

    'field.targetW': 'Target width',
    'field.targetH': 'Target height',
    'field.bind': 'Bind aspect ratio',
    'field.pixelSize': 'Pixel size',
    'field.thickness': 'Thickness',
    'field.mode': 'Downscale mode',
    'field.sharpenMode': 'Sharpen mode',
    'field.doColorMatch': 'Colour match',
    'field.doQuant': 'Colour quantisation',
    'field.numColors': 'Colours',
    'field.quantMode': 'Quantisation mode',
    'field.ditherMode': 'Dithering',
    'field.noPostUpscale': 'Output raw small image',
    'field.output': 'Output size',

    'thickness.off': 'off',
    'presets': 'Presets',
    'preset.default': 'Default',
    'preset.gba': 'GBA look',
    'preset.detail': 'High detail',
    'preset.lofi': 'Lo-fi',

    'action.upload': 'Upload image',
    'action.open': 'Open image',
    'action.exportPng': 'Export PNG',
    'action.exportWebp': 'Export WebP',
    'action.copy': 'Copy',
    'action.copied': 'Copied',
    'action.copyFailed': 'Copy failed',
    'action.shareUrl': 'Params → URL',
    'action.urlCopied': 'Link copied',
    'action.reset': 'Reset',

    'drop.title': 'Drop an image, or click to browse',
    'drop.hint': 'Drag and drop, or paste with Ctrl+V. PNG / JPEG / WebP',
    'drop.examples': 'Or try an example',
    'drop.needImage': 'Load an image to begin',

    'viewer.holdForOriginal': 'Hold space for the original',
    'viewer.splitHint': 'Drag the divider to compare',
    'viewer.zoomIn': 'Zoom in',
    'viewer.zoomOut': 'Zoom out',
    'viewer.resetZoom': 'Reset to 100%',
    'viewer.fit': 'Fit to window',
    'viewer.original': 'Original',
    'viewer.result': 'Result',

    'status.size': 'Size',
    'status.time': 'Frame',
    'status.working': 'Working',
    'status.zoom': 'Zoom',
    'status.exact': 'exact',
    'status.reduced': 'memory-capped',
    'status.clamped': 'working resolution lowered to fit the GPU memory budget',
    'status.exporting': 'exporting',
    'status.rendering': 'rendering',
    'status.iterations': 'k-means iters',

    'error.title': 'Something went wrong',
    'error.unsupported': 'Your browser cannot render to floating point textures',
    'error.unsupportedHint':
      'This app needs WebGL2 with the EXT_color_buffer_float extension. Please update your browser or use desktop Chrome, Firefox or Safari.',

    'help.title': 'Shortcuts',
    'help.original': 'Hold to see the original',
    'help.fit': 'Fit to window',
    'help.actual': 'Actual pixels (100%)',
    'help.zoom': 'Zoom in / out, 20 levels',
    'help.export': 'Export PNG',
    'help.wheel': 'Wheel',
    'help.wheelHint': 'Zoom',
    'help.drag': 'Drag',
    'help.dragHint': 'Pan the image',
    'help.divider': 'Divider',
    'help.dividerHint': 'Compare before / after',
    'action.close': 'Close',

    'shortcut.space': 'Space',
    'shortcut.fit': 'F',
    'shortcut.actual': '1',
    'shortcut.export': 'Ctrl+E',
    'shortcuts': 'Shortcuts',
  },
} as const;

export type StringKey = keyof (typeof STRINGS)['en'];

function detectLocale(): Locale {
  const langs = typeof navigator !== 'undefined' ? navigator.languages ?? [] : [];
  for (const l of langs) {
    if (l.toLowerCase().startsWith('zh')) return 'zh';
  }
  return typeof navigator !== 'undefined' && navigator.language?.startsWith('zh')
    ? 'zh'
    : 'en';
}

let current: Locale = detectLocale();
const listeners = new Set<(l: Locale) => void>();

export function getLocale(): Locale {
  return current;
}

export function setLocale(loc: Locale): void {
  if (loc === current) return;
  current = loc;
  for (const fn of listeners) fn(loc);
}

export function t(key: StringKey, loc: Locale = current): string {
  return STRINGS[loc][key] ?? STRINGS.en[key] ?? key;
}

/** Subscribe a component to the active locale. */
export function useT(): (key: StringKey) => string {
  const [loc, setLoc] = useState(current);
  useEffect(() => {
    listeners.add(setLoc);
    return () => {
      listeners.delete(setLoc);
    };
  }, []);
  return useCallback((key: StringKey) => t(key, loc), [loc]);
}
